import { IDataSetColumn } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { MenuItem } from '@adaptiva/core/menus';
import { OverlayService } from '@adaptiva/core/overlays';
import { IRowEvent, SchemaAndRows } from '@adaptiva/core/tables';
import { UiService } from '@adaptiva/core/ui';
import { BaseWidgetComponent, TextConfig, WidgetService } from '@adaptiva/core/widgets';
import { DataSetColumn, EJavaClassNames, IObjectTableListConfiguration } from '@adaptiva/data-service';
import { IMetadataAuthoringLog, IMetadataHistoryObject, IMetadataObject, MetadataAuthoringManagerService, MetadataObject } from '@adaptiva/metadata/common';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { TableComponent } from '@adaptiva/widgets/table';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { KeyValueColorObject } from './key-value-color-object';
import { MetadataHistoryObjectRow } from './metadata-history-object-row';

@Component({
	standalone: false,
	selector: 'ada-metadata-history-object-table',
	templateUrl: './metadata-history-object-table.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./metadata-history-object-table.component.scss'],
	providers: [
		{
			provide: WidgetService,
			useClass: WidgetService
		},
		{
			provide: BaseWidgetComponent,
			useExisting: forwardRef(() => MetadataHistoryObjectTableComponent)
		}
	]
})
export class MetadataHistoryObjectTableComponent extends BaseWidgetComponent implements AfterViewInit {

	private _objectId: number;

	@Input()
	public rowsPerPage: number = 10;

	@Input()
	public object$: BehaviorSubject<IMetadataObject>;

	@ViewChild('ObjectHistoryOverlay')
	public objectHistoryOverlay: OverlayComponent;

	@ViewChild('HistoryTable')
	public historyTable: TableComponent;

	@Output()
	public reverted: EventEmitter<MetadataHistoryObjectRow> = new EventEmitter();

	public objectHistoryTableData$: BehaviorSubject<SchemaAndRows> = new BehaviorSubject(null);

	public overlayProps: KeyValueColorObject[] = new Array<KeyValueColorObject>();

	private _pageNumber: int = 1;

	private _historyLength: int = 0;

	private _commitHistory: string[] = [];

	private _columnSchema: IDataSetColumn[] = [
		new DataSetColumn({
			columnName: 'description',
			columnDisplay: 'Description',
			type: 'string'
		}),
		new DataSetColumn({
			columnName: 'datetime',
			columnDisplay: 'Date',
			type: 'string'
		}),
		new DataSetColumn({
			columnName: 'userLogin',
			columnDisplay: 'User ID',
			type: 'string'
		}),
		new TextConfig({ columnName: 'description', columnDisplay: 'Description', type: 'string', isHeading: false }),
		new TextConfig({ columnName: 'commitId', columnDisplay: 'Commit ID', type: 'string', isHeading: false }),
		new TextConfig({ columnName: 'datetime', columnDisplay: 'Date Time', type: 'string', isHeading: false }),
		new TextConfig({ columnName: 'userLogin', columnDisplay: 'User Login', type: 'string', isHeading: false }),
		new TextConfig({ columnName: 'objectsInvolved', columnDisplay: 'Objects Involved', type: 'string', isHeading: false }),
		new TextConfig({ columnName: 'operationPerformed', columnDisplay: 'Operation Performed', type: 'string', isHeading: false }),
	];

	public objectHistoryQueryConfig: IObjectTableListConfiguration = {
		objectClassName: EJavaClassNames.MetadataObject,
		parentFolderId: 0,
		sortColumn: 'name',
		ascending: true
	};

	protected onRevertDisabled: BehaviorSubject<boolean> = new BehaviorSubject(false);

	protected readonly historyTableMenus: MenuItem<IRowEvent<MetadataHistoryObjectRow>>[] = [
		new MenuItem({
			name: 'Revert',
			isDisabled: this.onRevertDisabled,
			onSelect: (_menu: MenuItem<IRowEvent<MetadataHistoryObjectRow>>) => this.handleRevert(_menu.data)
		}),
		new MenuItem({
			name: 'View',
			onSelect: (_menu: MenuItem<IRowEvent<MetadataHistoryObjectRow>>) => this.handleView(_menu.data)
		})
	];

	constructor(
		widgetService: WidgetService,
		private metadataAuthoringManager: MetadataAuthoringManagerService,
		public ui: UiService,
		public logger: LoggingService,
		public overlayService: OverlayService
	) {
		super(widgetService);
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		this.object$?.pipe(switchMap((_object: MetadataObject): Observable<int> => {
			this._objectId = _object?.objectId;

			if (this._objectId)
				return this.metadataAuthoringManager.getObjectHistorySize(this._objectId);

			return of(0);
		}), takeUntil(this.destroyed$)).subscribe({
			next: (_length: int) => {
				this._historyLength = _length;
				this._initTable();
			}
		});
	}

	private _initTable(): void {
		if (this._objectId) {
			this._commitHistory = [];
			this.fetchObjectHistory((_historyObjectArray: IMetadataHistoryObject[]) => {
				this._commitHistory.push(_historyObjectArray[_historyObjectArray.length - 1].commitId);
			});
		}
		// Else, there is no data to fetch, which will default to the custom message.
	}

	public handleView(_rowEvent: IRowEvent<MetadataHistoryObjectRow>) {
		const _selection: MetadataHistoryObjectRow = (_rowEvent.row as MetadataHistoryObjectRow);

		this.overlayProps = [];

		// add new entries first
		for (const [_key, _value] of Object.entries(_selection.propertiesAdded)) {
			const _entry = new KeyValueColorObject(_key, _value, 'added');

			this.overlayProps.push(_entry);
		}

		// fill in colors of already existing objects
		for (const [_key, _value] of Object.entries(_selection.object.authoringProperties)) {
			const _entry: KeyValueColorObject = new KeyValueColorObject(_key, _value);

			if (_selection.propertiesChanged[_key]) { // changed properties, create two entries in overlay
				_entry.color = 'removed';
				this.overlayProps.push(_entry);
				const _newEntry = new KeyValueColorObject(_key, _selection.propertiesChanged[_key][1]);

				_newEntry.color = 'added';
				this.overlayProps.push(_newEntry);
			} else if (_selection.propertiesRemoved.includes(_key)) { // removed properties
				_entry.color = 'removed';
				this.overlayProps.push(_entry);
			} else { // unchanged properties
				_entry.color = '';
				this.overlayProps.push(_entry); // no color
			}
		}
		this.objectHistoryOverlay.open();
	}

	/**
	 * Listens for page changes and will fetch the next data based on commit history.
	 * 
	 * @param _newPage The next page number to reach.
	 */
	public handleCurrentPageChanged(_newPage: number): void {
		if (this._pageIncreased(_newPage)) {
			this.fetchObjectHistory((_historyObjectArray: IMetadataHistoryObject[]) => {
				this._commitHistory.push(this._fromArrayEnd(_historyObjectArray).commitId);
			}, this._fromArrayEnd(this._commitHistory));
		}
		else {
			this._commitHistory.pop();

			this.fetchObjectHistory((_historyObjectArray: IMetadataHistoryObject[]) => {
			}, this._fromArrayEnd(this._commitHistory, 1));
		}

		this._pageNumber = _newPage;
	}

	/**
	 * On row change, clear the history and update the page number
	 * 
	 * @param _newRowsPerPage The new number of rows per page.
	 */
	public handleRowsPerPageChanged(_newRowsPerPage: int): void {
		this.rowsPerPage = _newRowsPerPage;
		this._initTable();
	}

	/**
	 * Fetches object history of the current object.
	 * 
	 * @param afterFetch Callback to be performed after fetching the history and before the table has been updated.
	 * @param previousCommit The offset commit to retrieve paginated data from
	 */
	public fetchObjectHistory(afterFetch: (history: IMetadataHistoryObject[]) => void): void;
	public fetchObjectHistory(afterFetch: (history: IMetadataHistoryObject[]) => void, previousCommit: string): void;
	public fetchObjectHistory(_afterFetch: (history: IMetadataHistoryObject[]) => void, _previousCommit?: string): void {
		if (!this._objectId)
			return;

		this.historyTable?.tableState.setIsFetching(true);

		this.metadataAuthoringManager.getObjectHistory(this._objectId, this.rowsPerPage, _previousCommit).pipe(
			tap({
				next: (_historyObject: IMetadataHistoryObject[]) => {
					_afterFetch(_historyObject);
				}
			})).subscribe({
				next: (_objectHistory: IMetadataHistoryObject[]) => {
					const _rows: MetadataHistoryObjectRow[] = this._historyObjectsToRows(_objectHistory);
					const _tableData: SchemaAndRows = new SchemaAndRows(
						this._columnSchema,
						[..._rows],
						this._historyLength
					);

					this.objectHistoryTableData$.next(_tableData);
				},
				error: (_error: any) => {
					const _errorMessage: string = this.logger.extractErrorMessage(_error);

					this.logger.error(
						'MetadataHistoryObjectTableComponent.fetchObjectHistory',
						'Error retrieving history object',
						_errorMessage
					)();
					this.historyTable?.tableState.setIsFetching(false, new Error(_errorMessage));
				}
			});
	}

	private _historyObjectsToRows(historyObjects: IMetadataHistoryObject[]): MetadataHistoryObjectRow[] {
		return historyObjects.map(historyObject => {
			const _flattendObject: MetadataHistoryObjectRow = new MetadataHistoryObjectRow();

			_flattendObject.commitId = historyObject.commitId;
			_flattendObject.datetime = historyObject.authoringLog.datetime;
			_flattendObject.userLogin = historyObject.authoringLog.userLogin;
			_flattendObject.objectsInvolved = historyObject.authoringLog.objectsInvolved;
			_flattendObject.operationPerformed = IMetadataAuthoringLog.getOperationName(
				historyObject.authoringLog.operationPerformed
			);
			_flattendObject.description = historyObject.authoringLog.description;
			_flattendObject.propertiesRemoved = historyObject.propertiesRemoved;
			_flattendObject.propertiesAdded = historyObject.propertiesAdded;
			_flattendObject.propertiesChanged = historyObject.propertiesChanged;
			_flattendObject.object = historyObject.object as MetadataObject;

			return _flattendObject;
		}) as MetadataHistoryObjectRow[];
	}

	public handleRevert(_rowEvent: IRowEvent<MetadataHistoryObjectRow>): void {
		this.reverted.emit(_rowEvent.row);
	}

	protected disableRowMenus(_isOpen: boolean): void {
		if (_isOpen)
			this.onRevertDisabled.next(this.isDisabled);
	}

	/**
	 * Determines direction of user pagination.
	 * 
	 * @param _newPageNumber The new page number we areon
	 * @returns True if we incremented to a new page, false if we decremented
	 */
	private _pageIncreased(_newPageNumber: int): boolean {
		return _newPageNumber > this._pageNumber;
	}

	/**
	 * Retrieve elements from an array indexed from the back
	 * 
	 * @param _array The targeted array
	 * @param _offset Index from the end of the array
	 * @returns The value at the specified offset, or null if out of bounds.
	 */
	private _fromArrayEnd<T>(_array: T[], _offset: int = 0): T {
		if (_array.length - 1 - _offset < 0)
			return null;

		return _array[_array.length - 1 - _offset];
	}

}
